import { computed, onBeforeUnmount, onMounted, watch } from "vue";
import { useAuthStore } from "~/stores/auth";
import {
  canStudentAccessLearning,
  nextStudentAccessBoundary,
} from "~/utils/studentAccess";

export function useStudentAccess() {
  const auth = useAuthStore();
  return {
    canAccessLearning: computed(() =>
      canStudentAccessLearning(auth.user.studentAccess),
    ),
    trialAccess: computed(() => auth.user.studentAccess.trialAccess),
  };
}

/** Mount once at the application root, including while a call stays open. */
export function useStudentAccessRevalidation() {
  const auth = useAuthStore();
  let mounted = false;
  let refreshing = false;
  let refreshRequested = false;
  let boundaryTimer: ReturnType<typeof setTimeout> | undefined;
  let pollTimer: ReturnType<typeof setInterval> | undefined;
  const context = () =>
    auth.profileLoaded && auth.user.permissions.isStudent
      ? `${auth.user.id}:${auth.user.studentId}:${auth.user.activeOrganization?.id ?? ""}`
      : null;

  function scheduleBoundary() {
    clearTimeout(boundaryTimer);
    boundaryTimer = undefined;
    if (!mounted || !context()) return;
    const boundary = nextStudentAccessBoundary(
      auth.user.studentAccess.trialAccess,
    );
    if (boundary === null) return;
    boundaryTimer = setTimeout(
      () => {
        refreshAccess();
      },
      Math.min(Math.max(boundary - Date.now() + 50, 0), 2_147_483_647),
    );
  }

  async function refreshAccess() {
    const currentContext = context();
    if (!mounted || !currentContext) return;
    if (refreshing) {
      refreshRequested = true;
      return;
    }
    refreshing = true;
    try {
      await auth.fetchUserData();
    } catch {
      // HTTP and realtime access remain enforced by the backend on failures.
    } finally {
      refreshing = false;
      const shouldRefreshAgain = refreshRequested;
      refreshRequested = false;
      if (mounted && currentContext === context()) {
        scheduleBoundary();
        // A boundary reached during an earlier request needs a fresh snapshot.
        if (shouldRefreshAgain) refreshAccess();
      }
    }
  }

  function refreshVisibleAccess() {
    if (document.visibilityState !== "hidden") refreshAccess();
  }

  watch(
    () => [
      context(),
      auth.user.studentAccess.trialAccess.current?.ends_at,
      auth.user.studentAccess.trialAccess.next?.starts_at,
    ],
    scheduleBoundary,
  );

  onMounted(() => {
    mounted = true;
    scheduleBoundary();
    refreshVisibleAccess();
    pollTimer = setInterval(refreshVisibleAccess, 30_000);
    window.addEventListener("focus", refreshVisibleAccess);
    document.addEventListener("visibilitychange", refreshVisibleAccess);
  });
  onBeforeUnmount(() => {
    mounted = false;
    clearTimeout(boundaryTimer);
    clearInterval(pollTimer);
    window.removeEventListener("focus", refreshVisibleAccess);
    document.removeEventListener("visibilitychange", refreshVisibleAccess);
  });
}
